<template>
  <div>
    <div class="row pt-3 l-m-b-20">
      <div class="col-9">
        <h1>Companies</h1>
      </div>
      <div class="col-3 text-right" v-if="$_can('companies.store')">
        <router-link to="/companies/create" tag="button" class="btn btn-primary">
          ADD COMPANY
        </router-link>
      </div>
    </div>
    <div class="row">
      <div class="col-12 l-m-b-20">
        <div class="input-group search">
          <button type="button" class="btn input-group-btn" @click="clearSearch">
            <i :class="[q ? 'fa fa-remove' : 'fa fa-search']" />
          </button>
          <input class="form-control" type="text" v-model="q" @keyup="searchText($event)" placeholder="Search" />
        </div>
      </div>
      <div class="col-12 filters-field">
        <span>Filters:</span>
        <span v-if="filtersLoaded">
          <span v-if="filters.carrier.length">
            <span v-for="carrier in filters.carrier" :key="carrier" class="filter-item btn btn-filter">
              <span class="mod-icon-filter" @click="removeFilter('carrier', carrier)" />
              Carrier: {{ carriers.find(el => el.id === carrier).name }}</span>
          </span>
          <span v-if="filters.companyStatus.length">
            <span v-for="companyStatus in filters.companyStatus" :key="companyStatus" class="filter-item btn btn-filter">
              <span class="mod-icon-filter" @click="removeFilter('companyStatus', companyStatus)" />
              Status: {{ companyStatuses.find(el => el.id === companyStatus).name }}</span>
          </span>
          <span v-if="filters.tags && filters.tags.length && tags">
            <span v-for="tag in filters.tags" :key="tag" class="filter-item btn btn-filter">
              <span class="mod-icon-filter" @click="removeFilter('tags', tag)" />
              Tag: {{ tags.find(el => el.id === tag).name }} </span>
          </span>
        </span>
        <button class="btn btn-secondary add-filters" @click="showFilters = true">
          <i class="fa fa-plus filter-icon" />
        </button>
      </div>
    </div>
    <template v-if="!companies">
      <vue-content-loading :width="800" :height="450">
        <rect v-for="x in 15" :key="x" x="10" :y="15*x*1.4" rx="5" ry="5" width="720" height="6" />
      </vue-content-loading>
    </template>
    <template v-else>
      <table class="table m-table table-hover l-m-t-20">
        <thead class="m-table__heading">
          <tr>
            <th class="m-table__id">ID</th>
            <th @click="sortBy('name')" :class="{ active: sortKey === 'name' }" class="sortable">
              Name
              <span class="arrow" :class="sortOrders['name'] ? 'dsc' : 'asc'" />
            </th>
            <th>Address</th>
            <th>Deposit</th>
            <th>Min Hours</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="company in companies" @mouseup="$_can('companies.show') && view($event, company.id)"
              class="pointer"
              :key="company.id"
          >
            <td>{{ company.id }}</td>
            <td>{{ company.name }}</td>
            <td>{{ company.address }}</td>
            <td>${{ company.deposit_value }}</td>
            <td>{{ company.minimum_hours }}</td>
            <td>{{ company.status.name }}</td>
          </tr>
          <tr v-if="companies && !companies.length">
            <td colspan="6" class="text-center">
              No results found
            </td>
          </tr>
        </tbody>
      </table>

      <div class="row">
        <div class="col-sm-6 mb-1 text-black-50"
             v-if="meta.total"
        >
          Showing {{ meta.from }} - {{ meta.to }} of {{ meta.total }}
        </div>
        <pagination class="col-sm-6 mb-1" :change-page="changePage" :meta="meta" />
      </div>
    </template>

    <!-- Filters -->
    <transition name="slide-fade">
      <div class="l-modal-mask" v-if="showFilters" @click.self="showFilters = false">
        <div v-if="showFilters" class="filters-side-modal flex flex-column">
          <div @click="showFilters = !showFilters" class="pointer">
            <h1 class="modal-h1 l-m-b-20"><span class="btn btn-back" />Add Filter</h1>
          </div>
          <div>
            <p>
              Filters are applied automatically. You can remove them one by one with minus button or with clear filters button.
            </p>
          </div>
          <div class="l-bg-white">
            <div class="row">
              <div class="col">
                <div class="row filters">
                  <div class="col-12">
                    <div class="select-wrap">
                      <label>Carriers</label>
                      <key-multiselect v-model="filters.carrier"
                                       :options="carriers"
                                       :multiple="true"
                                       :clear-on-select="false"
                                       :hide-selected="true"
                                       :preserve-search="false"
                                       label="name"
                                       track-by="id"
                                       @input="search"
                      >
                        <template slot="tag" slot-scope="props">
                          <div class="multiselect__tag">
                            <span>{{ props.option.name }}</span>
                            <span class="multiselect__tag-icon" @click="props.remove(props.option)" />
                          </div>
                        </template>
                      </key-multiselect>
                    </div>
                  </div>
                  <div class="col-12">
                    <div class="select-wrap">
                      <label>Company Status</label>
                      <key-multiselect v-model="filters.companyStatus"
                                       :options="companyStatuses"
                                       :multiple="true"
                                       :clear-on-select="false"
                                       :hide-selected="true"
                                       :preserve-search="false"
                                       label="name"
                                       track-by="id"
                                       @input="search"
                      >
                        <template slot="tag" slot-scope="props">
                          <div class="multiselect__tag">
                            <span>{{ props.option.name }}</span>
                            <span class="multiselect__tag-icon" @click="props.remove(props.option)" />
                          </div>
                        </template>
                      </key-multiselect>
                    </div>
                  </div>
                  <div class="col-12">
                    <div class="select-wrap">
                      <label>Tags</label>
                      <key-multiselect
                        v-model="filters.tags"
                        :options="tags"
                        :multiple="true"
                        :clear-on-select="false"
                        :close-on-select="false"
                        track-by="id"
                        @input="search"
                        label="name"
                        class="w-100"
                      >
                        <template slot="tag" slot-scope="props">
                          <span class="multiselect__tag p-0 bg-transparent">
                            <span>
                              <tag-color

                                :color="props.option.color.name"
                                :name="props.option.name"
                              />
                            </span>
                          </span>
                        </template>
                        <template slot="option" slot-scope="props">
                          <div class="option__desc">
                            <div class="d-flex align-items-center justify-content-between">
                              <span class="option__title">
                                {{ props.option.name }}
                              </span>
                            </div>
                          </div>
                        </template>
                      </key-multiselect>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="row mt-auto">
            <div class="col">
              <button type="button" class="btn btn-not-active text-dark w-100" @click="clearFilters"
                      v-if="!(!filters.carrier.length && !filters.companyStatus.length && !filters.tags.length)"
              >
                Clear filters
              </button>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState, mapMutations } from 'vuex';
import axios from 'axios';
import Pagination from '../Pagination';
import TagColor from '../settings/Tags/TagColor';

export default {
  name: 'Companies',
  components: { TagColor, Pagination },
  data: () => ({
    filters: {
      carrier: [],
      companyStatus: [1],
      tags: [],
    },
    sortKey: '',
    sortOrders: [],
    page: 1,
    q: '',
    showFilters: false,
    tags: [],
  }),
  computed: {
    ...mapState({
      filtersLoaded: state => state.guard.filtersLoaded,
      companies: state => state.companies.paginatedCompanies.data,
      meta: state => state.companies.paginatedCompanies.meta,
      carriers: state => state.carriers.carriers.data,
      companyStatuses: state => state.values.companyStatuses,
      filtersSaved: state => state.filters.filtersSaved,
    }),
    ...mapGetters(['getCompaniesFiltered', 'getFiltersSaved']),
  },
  mounted() {
    this.getTags();
    if (this.filtersSaved && this.filtersSaved.previousPage === 'CompaniesEdit') {
      this.filters = { ...this.filtersSaved };
      this.page = this.filtersSaved.page;
      this.filter();
    } else {
      if (Object.keys(this.$route.query).length !== 0) {
        this.setFilters(this.$route.query);
      }
      this.filter();
    }
  },
  methods: {
    ...mapActions(['getPaginatedCompanies']),
    ...mapMutations(['setPaginatedCompanies', 'setFiltersSaved']),
    getTags() {
      axios
        .get('/companies/tags/list?paginated=false')
        .then(res => {
          this.tags = res.data.data;
        })
        .catch(() => {});
    },
    setFilters(query) {
      this.filters = Object.assign(
        {
          carrier: query.default_carrier_id
            ? query.default_carrier_id.split(',').map(i => Number(i))
            : [],
          companyStatus: query.status_id ? query.status_id.split(',').map(i => Number(i)) : [],
          tags: query.tag_id ? query.tag_id.split(',').map(i => Number(i)) : [],
        },
        {},
      );
      this.sortKey = query.sort;
      this.sortOrders = query.order;
      this.page = query.page;
      this.q = query.q.toString();
    },
    view(event, id) {
      if (event.button === 1) {
        window.open(this.$router.resolve(`/companies/edit/${id}`).href, '_blank');
      } else {
        this.$router.push(`/companies/edit/${id}`);
      }
    },
    changePage(pageNum) {
      this.setPaginatedCompanies({});
      this.page = pageNum;
      this.filter();
    },
    searchText(e) {
      this.page = 1;
      if (/^[a-zA-Z0-9]$/.test(e.key) || e.keyCode === 8 || e.keyCode === 13) {
        const vm = this;
        clearTimeout(this.timer);
        this.timer = setTimeout(() => {
          vm.filter();
        }, 500);
      }
    },
    search() {
      this.page = 1;
      this.filter();
    },
    filter() {
      const query =
        `page=${this.page}&default_carrier_id=${this.filters.carrier}&status_id=${
          this.filters.companyStatus
        }` +
        `&q=${encodeURIComponent(this.q)}` +
        `&sort=${this.sortKey}&order=${this.sortOrders[this.filters.sortKey] ? 'asc' : 'desc'}` +
        `&tag_id=${this.filters.tags}`;
      this.getCompaniesFiltered(query);
      if (this.$_checkProperties(this.filters)) {
        this.$router.push(`/companies?${query}`);
      } else {
        this.$router.push(`/companies`);
      }
    },
    clearFilters() {
      this.filters = Object.assign(
        {
          carrier: [],
          companyStatus: [],
          tags: [],
        },
        {},
      );
      this.sortKey = '';
      this.sortOrders = [];
      this.filter();
      this.showFilters = false;
    },
    clearSearch() {
      this.q = '';
      this.filter();
    },
    sortBy(key) {
      this.sortKey = key;
      this.$set(this.sortOrders, key, !this.sortOrders[key]);
      this.filter();
    },
    removeFilter(model, id) {
      this.filters[model] = this.filters[model].filter(element => element !== id);
      this.search();
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/components/companies/Companies.vue